/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  FunctionField,
  DateField,
  useTranslate,
  useNotify
} from "react-admin";
import { ListItemButton, Theme, useMediaQuery } from "@mui/material";
import * as User from "providers/user";
import * as Axios from "providers/axios";
import { Link, useLocation } from "react-router-dom";
import { EmptyCustomersIcon } from "assets/Icons";
import UserAvatarAndName from "./UserAvatarAndName";
import ListDotsActions from "./ListDotsActions";
import CustomList from "./CustomList";
import CustomDataGrid from "./CustomDataGrid";
import { CustomButton } from "./CustomButton";
import CustomSearch from "./CustomSearch";
import { getPhone } from "./PhoneInput";

export default function UserList() {
  const { permissions } = User.useUser();
  const { api } = Axios.useAxios();
  const translate = useTranslate();
  const notify = useNotify();
  const router = useLocation();
  const resource = router.pathname.split("/")[2];
  const isSmall = useMediaQuery<Theme>("(max-width:800px)");
  const [disabledButtons, setDisabledButtons] = useState<{
    [key: string]: boolean;
  }>({});
  const [timers, setTimers] = useState<{ [key: string]: number }>({});
  const intervalsRef = useRef<{ [key: string]: NodeJS.Timeout }>({});

  const clearTimerInterval = (userId: string) => {
    if (intervalsRef.current[userId]) {
      clearInterval(intervalsRef.current[userId]);
      delete intervalsRef.current[userId];
    }
  };

  useEffect(() => {
    const loadStoredTimers = () => {
      try {
        const storedData = localStorage.getItem("resendEmailData");
        if (!storedData) return;

        const parsedData = JSON.parse(storedData);
        const now = Date.now();

        Object.entries(parsedData).forEach(([userId, data]: [string, any]) => {
          const endTime = data.endTime;
          const remainingSeconds = Math.ceil((endTime - now) / 1000);

          if (remainingSeconds > 0) {
            setDisabledButtons((prev) => ({ ...prev, [userId]: true }));
            setTimers((prev) => ({ ...prev, [userId]: remainingSeconds }));

            clearTimerInterval(userId);

            intervalsRef.current[userId] = setInterval(() => {
              const currentTime = Date.now();
              const remaining = Math.ceil((endTime - currentTime) / 1000);

              if (remaining <= 0) {
                clearTimerInterval(userId);
                setDisabledButtons((prev) => {
                  const newDisabled = { ...prev };
                  delete newDisabled[userId];
                  return newDisabled;
                });
                setTimers((prev) => {
                  const newTimers = { ...prev };
                  delete newTimers[userId];
                  return newTimers;
                });

                const currentData = JSON.parse(
                  localStorage.getItem("resendEmailData") || "{}"
                );
                delete currentData[userId];
                localStorage.setItem(
                  "resendEmailData",
                  JSON.stringify(currentData)
                );
              } else {
                setTimers((prev) => ({ ...prev, [userId]: remaining }));
              }
            }, 1000);
          } else {
            const currentData = JSON.parse(
              localStorage.getItem("resendEmailData") || "{}"
            );
            delete currentData[userId];
            localStorage.setItem(
              "resendEmailData",
              JSON.stringify(currentData)
            );
          }
        });
      } catch (error) {
        localStorage.removeItem("resendEmailData");
      }
    };

    loadStoredTimers();

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      Object.keys(intervalsRef.current).forEach(clearTimerInterval);
    };
  }, []);

  const handleResendEmail = async (record: any) => {
    try {
      const userId = record.id;
      setDisabledButtons((prev) => ({ ...prev, [userId]: true }));

      await api.post(
        `${process.env.REACT_APP_API_CORE_URL}/v1/users/resend-email`,
        {
          id: userId,
          company_id: permissions.companyId
        }
      );

      clearTimerInterval(userId);

      const startTime = Date.now();
      const endTime = startTime + 60 * 1000;

      const currentData = JSON.parse(
        localStorage.getItem("resendEmailData") || "{}"
      );
      currentData[userId] = { endTime };
      localStorage.setItem("resendEmailData", JSON.stringify(currentData));

      setTimers((prev) => ({ ...prev, [userId]: 60 }));
      intervalsRef.current[userId] = setInterval(() => {
        const currentTime = Date.now();
        const remaining = Math.ceil((endTime - currentTime) / 1000);

        if (remaining <= 0) {
          clearTimerInterval(userId);
          setDisabledButtons((prev) => {
            const newDisabled = { ...prev };
            delete newDisabled[userId];
            return newDisabled;
          });
          setTimers((prev) => {
            const newTimers = { ...prev };
            delete newTimers[userId];
            return newTimers;
          });

          const updatedData = JSON.parse(
            localStorage.getItem("resendEmailData") || "{}"
          );
          delete updatedData[userId];
          localStorage.setItem("resendEmailData", JSON.stringify(updatedData));
        } else {
          setTimers((prev) => ({ ...prev, [userId]: remaining }));
        }
      }, 1000);
    } catch (error) {
      const userId = record.id;
      clearTimerInterval(userId);
      setDisabledButtons((prev) => {
        const newDisabled = { ...prev };
        delete newDisabled[userId];
        return newDisabled;
      });
      setTimers((prev) => {
        const newTimers = { ...prev };
        delete newTimers[userId];
        return newTimers;
      });

      const currentData = JSON.parse(
        localStorage.getItem("resendEmailData") || "{}"
      );
      delete currentData[userId];
      localStorage.setItem("resendEmailData", JSON.stringify(currentData));

      notify(error?.response?.data?.message, {
        type: "error",
        undoable: false,
        anchorOrigin: { vertical: "bottom", horizontal: "center" }
      });
    }
  };

  return (
    <>
      <CustomList
        resource={`Custom${resource}`}
        exporter={permissions.isArtist ? false : undefined}
        hasCreate={permissions.isArtist ? false : true}
        filters={[
          <CustomSearch label={translate("users.search")} source="q" alwaysOn />
        ]}
        title={translate("pagenames.customers")}
        emptyIcon={<EmptyCustomersIcon />}
        emptyTitle={translate("empty_list.no_customers_yet")}
        createButtonText={translate("empty_list.add_a_customer")}
        emptyListCreateSubtitle={translate("empty_list.do_you_want_to_add_one")}
        createResource={resource}
        sort={{ field: "created_at", order: "DESC" }}
      >
        <CustomDataGrid
          bulkActionButtons={false}
          rowClick={(id: string) => `/dashboard/${resource}/${id}/show`}
        >
          <UserAvatarAndName
            label={translate("users.customer")}
            clickable={false}
            withMarginTop
            nameStyles="line-clamp-2 break-all text-[14px] min-w-[100px] md:min-w-[70px] xl:min-w-[120px]"
            sortBy="first_name"
          />
          <TextField
            source="email"
            data-label={translate("users.email") + ":"}
            sx={{
              color: "rgba(48, 47, 46, 0.95)",
              textDecoration: "none",
              fontSize: "14px"
            }}
          />
          <FunctionField
            render={(record: any) => getPhone(record?.libphonenumber)}
            emptyText="-"
            label={translate("users.phone_number")}
            data-label={translate("users.phone_number") + ":"}
            source="phone_number"
            data-cy="customer-row"
            sx={{
              color: "rgba(48, 47, 46, 0.95)",
              textDecoration: "none",
              display: isSmall ? null : "block",
              minWidth: "70px"
            }}
            className={isSmall ? "" : "break-all"}
          />
          <TextField
            label={translate("users.pronoun")}
            data-label={translate("users.pronoun") + ":"}
            source="pronoun"
            emptyText="-"
            sx={{
              color: "rgba(48, 47, 46, 0.95)",
              textDecoration: "none"
            }}
          />
          <DateField
            label={translate("users.registered")}
            data-label={translate("users.registered") + ":"}
            source="created_at"
            sx={{
              color: "rgba(48, 47, 46, 0.95)",
              textDecoration: "none"
            }}
          />
          <FunctionField
            label={translate("users.has_logged_in")}
            data-label={translate("users.has_logged_in") + ":"}
            source="cognito_id"
            emptyText="-"
            sx={{
              color: "rgba(48, 47, 46, 0.95)",
              textDecoration: "none",
              display: isSmall ? null : "block",
              minWidth: "70px"
            }}
            className={isSmall ? "" : "break-all"}
            render={(record: any) => {
              return record?.cognito_id ? (
                <span className="pl-2">{translate("common.yes")}</span>
              ) : (
                <CustomButton
                  variant="tertiary"
                  text={
                    disabledButtons[record?.id]
                      ? `${translate("users.resend_invite_disabled")} (${
                          timers[record?.id] || 60
                        }s)`
                      : translate("users.resend_invite")
                  }
                  disabled={disabledButtons[record?.id]}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleResendEmail(record);
                  }}
                />
              );
            }}
          />
          {!isSmall && (
            <FunctionField
              render={(record: any) => {
                return (
                  <ListDotsActions>
                    <Link to={`/dashboard/${resource}/${record?.id}`}>
                      <ListItemButton
                        sx={{
                          fontSize: "14px",
                          fontFamily: "Inter",
                          "& .MuiListItemButton-root": {
                            fontSize: "14px",
                            fontFamily: "Inter"
                          }
                        }}
                      >
                        {translate("common.edit")}
                      </ListItemButton>
                    </Link>
                  </ListDotsActions>
                );
              }}
            />
          )}
          {isSmall && (
            <FunctionField
              render={(record: any) => (
                <Link
                  to={`/dashboard/${resource}/${record?.id}/show`}
                  style={{
                    width: "100%",
                    marginBottom: "10px"
                  }}
                >
                  <CustomButton
                    variant="secondary"
                    text={translate("common.view_details")}
                    cyId="view-details-button"
                    sx={{ width: "100%" }}
                  />
                </Link>
              )}
            />
          )}
        </CustomDataGrid>
      </CustomList>
    </>
  );
}
